import { supabase } from '../supabaseClient';
import { withSessionValidation } from '../../utils/apiErrorHandler';
import { validateSession } from '../../utils/sessionValidationUtil';

type AIProvider = 'gemini' | 'deepseek';

/**
 * AI API Client
 * Handles communication with edge functions for AI operations
 */
export class AIApiClient {
  private static readonly AI_PROVIDER: AIProvider = 'gemini';
  private static readonly EDGE_FUNCTION_NAME = 'ai-idea-generator';
  private static readonly EDGE_FUNCTION_TIMEOUT = 45000;

  private static readonly fallbackModels = {
    gemini: [
      'gemini-2.5-flash',
      'gemini-2.5-pro',
      'gemini-2.5-flash-lite'
    ],
    deepseek: [
      'tngtech/deepseek-r1t2-chimera:free',
      'deepseek/deepseek-chat-v3.1:free',
    ]
  };

  /**
   * Call edge function to generate AI responses (secure API key handling)
   */
  static async callAI(
    prompt: string,
    operation: 'generate-ideas' | 'validation-questions' | 'task-checklist' = 'generate-ideas',
    options?: {
      language?: string;
      skills?: string[];
      interests?: string[];
      domains?: string[];
      industry?: string;
      includeTrends?: boolean;
    }
  ): Promise<string> {
    // Wrap the entire operation with session validation and error handling
    return withSessionValidation(async () => {
      // Get session (already validated by withSessionValidation)
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session. Please log in to use AI features.');
      }

      console.log(`🚀 Calling edge function for ${operation} with ${this.AI_PROVIDER}`);

      const { data, error } = await supabase.functions.invoke(this.EDGE_FUNCTION_NAME, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: {
          provider: this.AI_PROVIDER,
          prompt: prompt,
          operation: operation,
          temperature: 0.9,
          maxTokens: 4096,
          language: options?.language || 'en',
          skills: options?.skills || [],
          interests: options?.interests || [],
          domains: options?.domains || [],
          industry: options?.industry,
          includeTrends: options?.includeTrends !== false
        }
      });

      if (error) {
        console.error('❌ Edge function error:', error);
        throw new Error(`Edge function failed: ${error.message || JSON.stringify(error)}`);
      }

      if (!data || !data.success) {
        throw new Error(`Edge function returned unsuccessful response: ${JSON.stringify(data)}`);
      }

      console.log(`✅ Edge function succeeded with ${data.provider} in ${data.processingTime}ms`);
      return data.data;
    }, 'AIApiClient.callAI');
  }

  /**
   * Get current fallback models configuration
   */
  static getFallbackModels(): string[] {
    return [...this.fallbackModels[this.AI_PROVIDER]];
  }

  /**
   * Get current AI provider
   */
  static getProvider(): AIProvider {
    return this.AI_PROVIDER;
  }
}
